import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { CONSTANT } from '../../../../utils/constants';
import { FileUpload } from 'primereact/fileupload';
import FormInput from '../../../../components/Form/FormInput';
import { privateFetch } from '../../../../utils/apiHelper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useToastContext } from '../../../../contexts/ToastContext';
import moment from 'moment';
import slugify from 'react-slugify';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import { validateURL, validateYoutubeURL } from '../../../../utils/stringHelper';
import { computeImageShape } from '../../../../utils/imageHelper';
import { Message } from 'primereact/message';

function NewsPanel({
  news,
  loading,
  isOpen,
  onCreate,
  onUpdate,
  onClose,
  emptyPayload,
  isDuplicate
}) {
  const fileUploadRef = useRef(null);
  const { accountContext } = useAccountContext();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [publicationDate, setPublicationDate] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [image, setImage] = useState(undefined);
  const [videoUrl, setVideoUrl] = useState(undefined);
  const [imageLoading, setImageLoading] = useState(false);
  const [sourceLabel, setSourceLabel] = useState('');
  const { showToast } = useToastContext();
  const [imageIsValid, setImageIsValid] = useState(true);
  const [externalUrl, setExternalUrl] = useState('');
  const [labelUrl, setLabelUrl] = useState('');

  useEffect(() => {
    if (news) {
      setTitle(news.title);
      setContent(news.content);
      setPublicationDate(new Date(news.publication_date));
      setType(news.type);
      setVideoUrl(news.external_video_url);
      setImage(news.image);
      setSourceLabel(news.source_label);
      setExternalUrl(news.external_link_url);
      setLabelUrl(news.label_link_url);
    } else {
      setTitle('');
      setContent('');
      setPublicationDate(new Date());
      setType(CONSTANT.caeterra.newsTypes[0].value);
      setVideoUrl(undefined);
      setImage(undefined);
      setSourceLabel('');
      setExternalUrl('');
      setLabelUrl('');
    }
  }, [news, emptyPayload]);

  const handleValidate = () => {
    const newsPayload = {
      title,
      content,
      type,
      publication_date: moment(publicationDate).format('YYYY-MM-DD'),
      external_video_url: videoUrl,
      image: image.id,
      source_label: sourceLabel,
      external_link_url: externalUrl,
      label_link_url: labelUrl
    };
    news && !isDuplicate ? onUpdate(newsPayload) : onCreate(newsPayload);
  };

  const uploadImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setImage(res);
    });
    setImageLoading(false);
  };

  const onUpload = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClear();
    } else {
      setImageLoading(true);
      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadImage(imageForm);
    }
  };

  const onClear = () => {
    setImage(undefined);
    fileUploadRef.current.clear();
  };

  const validateButtonUrl = useMemo(
    () =>
      validateURL(externalUrl) &&
      ((labelUrl?.length > 0 && externalUrl?.length > 0) ||
        (!externalUrl?.length && !labelUrl?.length)),
    [externalUrl, labelUrl]
  );

  const formIsValid = () => {
    return (
      title.length > 3 &&
      content.length > 3 &&
      image &&
      sourceLabel?.length > 3 &&
      validateButtonUrl
    );
  };

  const handleImageLoad = (event) => {
    const isValid = computeImageShape(event);
    setImageIsValid(isValid);
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider l'article`}
        loading={loading}
        disabled={!formIsValid()}
        onClick={handleValidate}
        severity="success"
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: '80vw' }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${news ? 'Modifier' : 'Ajouter'} une actualité`}</h3>
      }>
      <div className="sidebar-content-container">
        <div className="grid grid-cols-2 gap-5 h-full overflow-y-hidden">
          <div className="flex flex-col gap-y-3 ">
            <FormInput
              key="content"
              label={'Contenu'}
              required
              isValid={content.length > 3}
              helpMessage={'Maintenez la touche Alt enfoncée pour conserver le ratio des images'}
              className="grow">
              <RichTextEditor
                value={content}
                onChange={(value) => {
                  setContent(value);
                }}
                acceptImage
                acceptHeaders
                height={'65vh'}
              />
            </FormInput>
            {footerButtons}
          </div>
          <div className="form-wrapper overflow-y-scroll h-auto">
            <FormInput
              label={'Titre (utilisé également pour le Titre SEO)'}
              required
              isValid={title.length > 3}>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre de l'article"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>

            <FormInput label={"Type d'article"} required isValid={type}>
              <Dropdown
                id="type"
                value={type}
                onChange={(e) => setType(e.value)}
                options={CONSTANT.bassecour.newsTypes}
                optionLabel="label"
                placeholder="Veuillez sélectionner le type d'article"
              />
            </FormInput>

            <FormInput label={"Auteur de l'article"} required isValid={sourceLabel?.length > 3}>
              <InputText
                id="source_label"
                value={sourceLabel}
                maxLength={300}
                placeholder="Veuillez saisir l'auteur de l'article"
                onChange={(e) => setSourceLabel(e.target.value)}
              />
            </FormInput>
            <FormInput
              label={'Url de la vidéo Youtube'}
              helpMessage={'Utiliser la fonction "Partager la vidéo" de Youtube'}
              errorMessage={
                !validateYoutubeURL(videoUrl) &&
                "L'URL doit avoir la forme https://youtu.be/HSQq_zEeEcM?si=4qk_KCcecxC95Zq"
              }>
              <InputText
                id="website_url"
                value={videoUrl}
                maxLength={300}
                placeholder="Veuillez saisir l'url de la vidéo Youtube"
                onChange={(e) => setVideoUrl(e.target.value)}
                invalid={!validateURL(videoUrl)}
              />
            </FormInput>
            <FormInput label={'Date de publication'} required isValid={publicationDate}>
              <Calendar
                id="publication-date"
                onChange={(e) => setPublicationDate(e.value)}
                dateFormat="dd/mm/yy"
                placeholder="Veuillez selectionner la date de publication"
                maxDate={new Date()}
                value={publicationDate}
                locale="fr"
              />
            </FormInput>
            <FormInput
              label={'Bouton'}
              errorMessage={
                !validateURL(externalUrl)
                  ? "L'URL doit commencer par http:// ou https://"
                  : !validateButtonUrl
                    ? 'Le bouton doit avoir un Url et un label'
                    : ''
              }>
              <div className="flex flex-row gap-x-3">
                <InputText
                  placeholder="Texte du bouton"
                  value={labelUrl}
                  onChange={(e) => setLabelUrl(e.target.value)}
                  className="grow"
                />

                <InputText
                  placeholder="https:// ou http://"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  className="grow"
                  invalid={!validateURL(externalUrl)}
                />
              </div>
            </FormInput>
            <FormInput
              key={'file'}
              helpMessage={'10Mo max.'}
              label={`Photo principale (max. 10Mo)`}
              required
              isValid={image}>
              <div className="flex flex-col gap-y-3">
                {!imageIsValid && image && (
                  <div className=" my-5">
                    <Message
                      severity="error"
                      text={`Cette image n'a pas le bon ratio (1,91:1) l'affichage sur le site vitrine ne sera optimal. Essayez avec un format 1920x1005 pixels.`}
                    />
                  </div>
                )}
                {image && (
                  <img
                    src={image.file}
                    className="w-[50%] object-cover "
                    alt="principale de l'évènement "
                    onLoad={handleImageLoad}
                  />
                )}
                <div className="flex flex-row gap-x-3">
                  {image && (
                    <Button
                      key={'delete-img'}
                      label={"Supprimer l'image"}
                      outlined
                      onClick={onClear}
                      severity="danger"
                    />
                  )}
                  {imageLoading ? (
                    <div className="w-[50px] h-[50px] p-1">
                      <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                    </div>
                  ) : (
                    <FileUpload
                      ref={fileUploadRef}
                      auto
                      disabled={image}
                      mode="basic"
                      name="file"
                      accept="image/*"
                      customUpload
                      uploadHandler={onUpload}
                      chooseLabel="Depuis mon ordinateur"
                    />
                  )}
                </div>
              </div>
            </FormInput>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}

export default NewsPanel;
